import { SublimatorClient } from "@wbly/client";
import { formatInitialEvent, InitialEvent } from "@wbly/common";
import { GTM, GTMNoScript } from "@wbly/gtm";
import { GetServerSidePropsContext, GetStaticPropsContext } from "next";
import { env } from "next-runtime-env";

import { CanvasWindow } from "env";

declare const window: CanvasWindow;

export type GtmProps = {
  initialEvent: InitialEvent;
};

/**
 * Helper function to get GTM props for both static and server-side rendered pages.
 * Can be composed with other getStaticProps or getServerSideProps functions.
 */
export async function getGtmProps(
  context: GetServerSidePropsContext | GetStaticPropsContext,
  pageType: string
): Promise<GtmProps> {
  const marketplaceIdentifier = context.params?.marketplace || "us";

  if (typeof marketplaceIdentifier !== "string") {
    throw new Error("Marketplace identifier must be a string");
  }

  const sublimatorApiKey = env("SUBLIMATOR_API_KEY");
  const sublimatorApiUrl = env("NEXT_PUBLIC_SUBLIMATOR_API_URL");

  if (!sublimatorApiKey || !sublimatorApiUrl) {
    throw new Error("SUBLIMATOR_API_KEY and SUBLIMATOR_API_URL must be set");
  }

  const sublimatorClient = new SublimatorClient({
    apiKey: sublimatorApiKey,
    baseURL: sublimatorApiUrl,
    marketplace: marketplaceIdentifier,
    prismicRepo: "website",
  });

  const marketplaces = await sublimatorClient.getMarketplaces();

  const marketplace = marketplaces.data.find(
    (m) => m.identifier === marketplaceIdentifier
  );

  if (!marketplace) {
    throw new Error("Marketplace not found");
  }

  const initialEvent = formatInitialEvent("pdc", marketplace, pageType);

  return {
    initialEvent,
  };
}

export const pageview = (url: string) => {
  (window.dataLayer || []).push({
    event: "pageview",
    page: url,
  });
};

type GtmAdapterProps = {
  initialEvent: InitialEvent;
};

const getGtmVariables = (): {
  containerId: string;
  host: string;
  query?: string;
} | null => {
  const gtmHost = env("NEXT_PUBLIC_GTM_HOST");
  const gtmContainerId = env("NEXT_PUBLIC_GTM_CONTAINER_ID");
  const gtmParams = env("NEXT_PUBLIC_GTM_PARAMS");

  if (!gtmHost || !gtmContainerId) {
    return null;
  }

  return {
    host: gtmHost,
    containerId: gtmContainerId,
    query: gtmParams,
  };
};

/**
 * while we use a common GTM tag to load the GTM script and configure the various tracking
 * models used for the site, it's handy to define a single place for environment injection
 * to the GTM component
 *
 * something to note is that you may see this tag fire off twice while developing, this is
 * to do with Next's strict react mode configuration: https://github.com/vercel/next.js/issues/35822
 * this is only an issue with development and doesn't happen in staging nor prod
 */
export function GtmAdapter({ initialEvent }: GtmAdapterProps) {
  const gtmVariables = getGtmVariables();

  if (!gtmVariables) {
    return null;
  }

  return (
    <>
      <GTM
        {...gtmVariables}
        ecommerceEndpoint={`/${initialEvent.marketplace || "us"}/data/ecommerce`}
        initialDataLayer={[initialEvent]}
        reportErrorEndpoint={`/report-error`}
      />

      <GTMNoScript {...gtmVariables} />
    </>
  );
}
